/*
 * Routes clicks on the collection filter rail through the same in-page render
 * that the filter drawer uses, so a circle updates the grid without a reload,
 * and tracks whether the rail still has content to scroll to (which drives the
 * fade on its right edge).
 *
 * Every circle is a real link, so if this script (or facets.js) never loads,
 * clicking one still applies the filter via a normal page load.
 */
class UfFilterRail extends HTMLElement {
  constructor() {
    super();
    this.scroller = null;
    this.updateEdgeState = this.updateEdgeState.bind(this);
    this.addEventListener('click', this.onRailClick.bind(this));
  }

  connectedCallback() {
    this.bindScroller();
    // facets.js replaces this element's innerHTML on every filter change.
    this.contentObserver = new MutationObserver(() => this.bindScroller());
    this.contentObserver.observe(this, { childList: true });
    window.addEventListener('resize', this.updateEdgeState, { passive: true });
  }

  disconnectedCallback() {
    if (this.contentObserver) this.contentObserver.disconnect();
    if (this.scroller) this.scroller.removeEventListener('scroll', this.updateEdgeState);
    window.removeEventListener('resize', this.updateEdgeState);
  }

  bindScroller() {
    const next = this.querySelector('.uf-rail__scroller');
    if (next !== this.scroller) {
      if (this.scroller) this.scroller.removeEventListener('scroll', this.updateEdgeState);
      this.scroller = next;
      if (this.scroller) this.scroller.addEventListener('scroll', this.updateEdgeState, { passive: true });
    }
    this.syncPriceState();
    this.updateEdgeState();
  }

  /*
   * Price circles settle their own active state from the URL.
   *
   * Liquid cannot read query parameters, and the price bounds Shopify does expose
   * (`filter.max_value.value`, `filter.range_max`) describe the already-filtered
   * result set — so applying "under 10" moves the ceiling to 10 and any
   * server-side comparison to detect it becomes unreliable. The URL is not
   * ambiguous, so it decides. facets.js pushes state before swapping the DOM, so
   * location.search is current by the time this runs.
   */
  syncPriceState() {
    const priceItems = this.querySelectorAll('[data-uf-price-lte]');
    if (!priceItems.length) return;

    const search = window.location.search;
    const applied = new URLSearchParams(search).getAll('filter.v.price.lte');

    priceItems.forEach((item) => {
      const threshold = Number(item.dataset.ufPriceLte);
      const isActive = applied.some((value) => Number(value) === threshold);

      item.classList.toggle('uf-rail__item--active', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'true');
        // Re-point at the same view minus the price bounds, so the circle toggles.
        const remaining = new URLSearchParams(search);
        remaining.delete('filter.v.price.lte');
        remaining.delete('filter.v.price.gte');
        const query = remaining.toString();
        item.href = window.location.pathname + (query ? `?${query}` : '');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  updateEdgeState() {
    const scroller = this.scroller;
    if (!scroller) return;

    const overflows = scroller.scrollWidth - scroller.clientWidth > 2;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;

    this.toggleAttribute('data-no-overflow', !overflows);
    this.toggleAttribute('data-scroll-end', overflows && atEnd);
  }

  onRailClick(event) {
    const link = event.target.closest('a[data-uf-rail-link]');
    if (!link) return;

    // Leave new-tab / new-window intents to the browser.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button && event.button !== 0) return;

    // Filtering JS absent or grid missing: fall through to the href.
    if (typeof FacetFiltersForm === 'undefined') return;
    if (!document.getElementById('ProductGridContainer')) return;

    event.preventDefault();

    const href = link.href;
    const queryIndex = href.indexOf('?');
    FacetFiltersForm.renderPage(queryIndex === -1 ? '' : href.slice(queryIndex + 1));
  }
}

if (!customElements.get('uf-filter-rail')) {
  customElements.define('uf-filter-rail', UfFilterRail);
}
